/**
 * Card System Constants and Data Model
 *
 * Card Instance Shape:
 * @typedef {Object} CardInstance
 * @property {string} id - Unique instance ID (generated)
 * @property {string} cardId - Card definition ID (e.g., 'check-in')
 * @property {string} name - Display name
 * @property {string} rarity - One of RARITY values
 * @property {string} description - Card description
 * @property {Object} requirement - Completion requirement
 * @property {string} requirement.type - Requirement type ('streak', 'action', 'none', etc.)
 * @property {number} [requirement.target] - Target value for completion
 * @property {string} [requirement.action] - Action to track
 * @property {number} [requirement.count] - Count for action-based requirements
 * @property {number} reward - M reward on completion
 * @property {string} expirationDate - ISO date string when card expires
 * @property {number} progress - Current progress (starts at 0)
 * @property {boolean} isActive - True if in active slots, false if in vault
 */

// Rarity enum
export const RARITY = {
  COMMON: 'common',
  RARE: 'rare',
  EPIC: 'epic',
  LEGENDARY: 'legendary'
};

// Rarity configuration with expiration days and draw weights
export const RARITY_CONFIG = {
  [RARITY.COMMON]: {
    expirationDays: 7,
    weight: 60
  },
  [RARITY.RARE]: {
    expirationDays: 14,
    weight: 25
  },
  [RARITY.EPIC]: {
    expirationDays: 30,
    weight: 12
  },
  [RARITY.LEGENDARY]: {
    expirationDays: 90,
    weight: 3
  }
};

// Card slot limits
export const CARD_LIMITS = {
  ACTIVE_SLOTS: 5,
  VAULT_CAPACITY: 15
};

// Requirement type enum - defines how card completion is tracked
export const REQUIREMENT_TYPE = {
  STREAK: 'streak',           // Consecutive days of an action (login, deposit, no-withdrawal)
  ACTION: 'action',           // Count of specific actions (deposit, impulse-save, round-up)
  THRESHOLD: 'threshold',     // Reach a target value (deposit amount, total savings)
  COMPOUND: 'compound',       // Multiple conditions (complex cards)
  PASSIVE: 'passive'          // No direct action - triggers on events (shield, doubler)
};

// Action type enum - trackable game actions for card requirements
export const ACTION_TYPE = {
  LOGIN: 'login',             // Daily login
  DEPOSIT: 'deposit',         // Making a deposit
  IMPULSE_SAVE: 'impulse-save', // Logging an impulse save
  ROUND_UP: 'round-up',       // Round-up event
  WITHDRAWAL: 'withdrawal',   // Making a withdrawal (for negative tracking)
  NO_WITHDRAWAL: 'no-withdrawal' // No withdrawals (for streak tracking)
};
